import { forwardRef, useMemo } from 'react';
import type React from 'react';
import {
  MobileDatePicker,
  DatePicker as DesktopDatePicker,
  MobileDatePickerProps,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { TextField, TextFieldProps } from '../TextField';
import type { TextFieldProps as MuiTextFieldProps } from '@mui/material';

type BaseDatePickerProps = MobileDatePickerProps<Dayjs>;

/**
 * Props for the DatePicker component.
 * Extends MUI's DatePicker props with custom TextField integration.
 */
export interface DatePickerProps
  extends Omit<BaseDatePickerProps, 'slots' | 'slotProps' | 'value' | 'onChange'> {
  /**
   * Override the field slot rendered inside the picker.
   * When provided, TextField-specific props (label, error, helperText, textFieldProps,
   * data-testid, data-identifier, sx) are ignored — the custom field is responsible
   * for its own trigger UI. Use this to build custom-trigger pickers like DueDatePicker.
   */
  field?: React.ElementType;
  /** Props forwarded to the custom field slot. Only used when `field` is provided. */
  fieldProps?: Record<string, unknown>;
  /**
   * The selected date value. Accepts Dayjs, Date, ISO string, or null.
   * Invalid dates are normalized to null.
   * @default null
   * @example
   * ```tsx
   * // All these formats work:
   * value={dayjs('2024-01-15')}
   * value={new Date('2024-01-15')}
   * value="2024-01-15"
   * value={null}
   * ```
   */
  value?: Dayjs | Date | string | null;

  /**
   * Callback fired when the date changes.
   * Always returns a Dayjs object for valid dates, or null for invalid/cleared dates.
   * @param value - The new date value (Dayjs or null)
   */
  onChange?: (value: Dayjs | null) => void;

  /**
   * If true, renders MobileDatePicker (touch-optimized).
   * If false, renders DatePicker (desktop with keyboard navigation).
   * @default true
   */
  mobile?: boolean;

  /**
   * Label for the date picker TextField.
   */
  label?: string;

  /**
   * If true, displays the TextField in error state.
   * @default false
   */
  error?: boolean;

  /**
   * Helper text to display below the TextField.
   * Typically used for error messages or hints.
   */
  helperText?: string;

  /**
   * If true, disables the date picker and TextField.
   * @default false
   */
  disabled?: boolean;

  /**
   * Props to pass through to the underlying ui-library TextField.
   * Useful for customizing TextField behavior, styling, or event handlers.
   */
  textFieldProps?: Partial<TextFieldProps>;

  /**
   * Test ID for the TextField input element.
   * Useful for automated testing.
   */
  'data-testid'?: string;

  /**
   * Custom identifier for tracking or analytics.
   */
  'data-identifier'?: string;

  /**
   * Custom styling for the TextField using MUI's sx prop.
   */
  sx?: TextFieldProps['sx'];
}

/**
 * DatePicker component - A date selection input field.
 *
 * Wraps MUI X's MobileDatePicker (default) or DatePicker with integrated
 * TextField rendering and error state handling. Accepts flexible date value
 * formats (Dayjs, Date, string) and normalizes them internally.
 *
 * Uses the ui-library TextField component for consistent styling and UX
 * (including autoSelectOnFocus behavior).
 *
 * Provides its own LocalizationProvider with dayjs adapter, so no external
 * localization setup is required.
 *
 * @example
 * ```tsx
 * // Mobile picker with string value (default)
 * <DatePicker
 *   label="Select Date"
 *   value="2024-01-15"
 *   onChange={(date) => setSelectedDate(date)}
 * />
 *
 * // Desktop picker with Date object
 * <DatePicker
 *   mobile={false}
 *   label="Select Date"
 *   value={new Date()}
 *   onChange={(date) => setSelectedDate(date)}
 * />
 *
 * // With validation and Dayjs value
 * <DatePicker
 *   label="Birth Date"
 *   value={dayjs(birthDate)}
 *   onChange={setBirthDate}
 *   error={!!error}
 *   helperText={error?.message}
 *   minDate={dayjs('1900-01-01')}
 *   maxDate={dayjs()}
 * />
 * ```
 */
export const DatePicker = forwardRef<HTMLDivElement, DatePickerProps>((props, ref) => {
  const {
    value,
    onChange,
    mobile = true,
    label,
    error = false,
    helperText,
    disabled = false,
    textFieldProps,
    field,
    fieldProps,
    'data-testid': dataTestId,
    'data-identifier': dataIdentifier,
    sx,
    ...datePickerProps
  } = props;

  const normalizedValue = useMemo(
    () => (value && dayjs(value).isValid() ? dayjs(value) : null),
    [value]
  );

  const Picker = mobile ? MobileDatePicker : DesktopDatePicker;

  const pickerProps: Omit<BaseDatePickerProps, 'slots' | 'slotProps'> & {
    value?: Dayjs | null;
    onChange?: (value: Dayjs | null) => void;
  } = {
    ...datePickerProps,
    disabled,
  };

  if ('value' in props) {
    pickerProps.value = normalizedValue;
  }

  if (onChange) {
    pickerProps.onChange = onChange;
  }

  if (field) {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Picker
          {...(pickerProps as MobileDatePickerProps<Dayjs>)}
          slots={{ field }}
          slotProps={{ field: fieldProps }}
        />
      </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Picker
        {...(pickerProps as MobileDatePickerProps<Dayjs>)}
        slots={{ textField: TextField }}
        slotProps={{
          textField: {
            ...textFieldProps,
            ref,
            label,
            error,
            helperText,
            fullWidth: true,
            // MUI DateField (picker internals) merges textFieldProps.inputProps into the native
            // input but does not thread slotProps.htmlInput, so inputProps is required here
            // despite being deprecated — remove when MUI fixes DateField's slotProps threading.
            inputProps: {
              ...(textFieldProps?.inputProps as object | undefined),
              'data-testid': dataTestId,
              'data-identifier': dataIdentifier,
            },
            sx: sx ?? textFieldProps?.sx,
          } as unknown as MuiTextFieldProps,
        }}
      />
    </LocalizationProvider>
  );
});

DatePicker.displayName = 'DatePicker';
